/**
 * Aktenraum v2.8: Auszug für angehakte Akten-Dokumente ohne OnlyOffice-Tab.
 * Vite-frei — Unit-Tests via node --experimental-strip-types / portal_node_ts.
 * Kein Voll-PDF, kein neues PB, max. PORTAL_SYNTHESIS_MAX_SELECTED_DOCS.
 */

export const PORTAL_AKTE_LAZY_EXTRACT_MAX_CHARS = 2500;
export const PORTAL_AKTE_LAZY_EXTRACT_MARKER = 'akte-lazy-extract:selected_docs';
/** Client-Abort unter typischem Proxy-/n8n-Idle (~60s), damit der Spinner nicht ewig kreist. */
export const PORTAL_AKTE_LAZY_EXTRACT_TIMEOUT_MS = 50_000;
export const PORTAL_AKTE_OCR_POLL_INTERVAL_MS = 2_500;
/** Poll-Budget inkl. Chip-Watchdog — danach ehrlich „Scan ohne Text“, nie 1h Pending. */
export const PORTAL_AKTE_OCR_POLL_MAX_MS = 180_000;
/** Chip nach UI-Timeout, solange pdf2text-Job noch läuft. */
export const PORTAL_AKTE_LAZY_EXTRACT_RECOGNIZING_HINT = 'wird erkannt…';
export const PORTAL_AKTE_OCR_STARTED_MARKER = 'erkannt-start:';

const METADATA_ONLY_HINT = 'Volltext noch nicht geladen';
const PENDING_HINT = 'Auszug wird geladen';
const RECOGNIZING_HINT = PORTAL_AKTE_LAZY_EXTRACT_RECOGNIZING_HINT;
const FAILED_HINT = 'Auszug konnte nicht geladen';

export function isMetadataOnlySynthesisContext(text: string | null | undefined): boolean {
  return (text || '').includes(METADATA_ONLY_HINT);
}

export function isPendingAkteLazyExtractContext(text: string | null | undefined): boolean {
  return (text || '').includes(PENDING_HINT);
}

export function isRecognizingAkteLazyExtractContext(text: string | null | undefined): boolean {
  return (text || '').includes(`Hinweis: ${RECOGNIZING_HINT}`);
}

/** Chip ohne laufenden Poll oder nach Budget: Job tot, nicht ewig „wird erkannt…“. */
export function isStaleRecognizingAkteLazyExtractContext(
  text: string | null | undefined,
  nowMs = Date.now(),
  maxMs = PORTAL_AKTE_OCR_POLL_MAX_MS,
): boolean {
  if (!isRecognizingAkteLazyExtractContext(text)) return false;
  const m = new RegExp(`${PORTAL_AKTE_OCR_STARTED_MARKER}(\\d{10,16})`).exec(text || '');
  if (!m) return true;
  const started = Number(m[1]);
  if (!Number.isFinite(started) || started <= 0) return true;
  return nowMs - started >= maxMs;
}

export function expireStaleRecognizingAkteDocumentContext(
  text: string | null | undefined,
  nowMs = Date.now(),
): string | null | undefined {
  if (!text || !isStaleRecognizingAkteLazyExtractContext(text, nowMs)) return text;
  const title = /^Dokument: (.+)$/m.exec(text)?.[1]?.trim() || 'Dokument';
  const source = /^Quelle: (.+)$/m.exec(text)?.[1]?.trim() || null;
  return buildFailedAkteLazyExtractContext({
    title,
    nc_path: !source || source === '(unbekannt)' ? null : source,
    reason: 'Scan ohne auslesbaren Text',
  });
}

export function isFailedAkteLazyExtractContext(text: string | null | undefined): boolean {
  return (text || '').includes(FAILED_HINT);
}

/** Reason from `buildFailedAkteLazyExtractContext` — e.g. `401`, `Netzwerk`. */
export function akteLazyExtractFailureReason(text: string | null | undefined): string | null {
  if (!isFailedAkteLazyExtractContext(text)) return null;
  const m = /Auszug konnte nicht geladen werden \(([^)]+)\)/.exec(text || '');
  const reason = m?.[1]?.trim();
  return reason || 'fehlgeschlagen';
}

/** Echter Auszug (OO-Pfad oder Lazy-Extract), kein Platzhalter. */
export function hasUsableAkteExtract(text: string | null | undefined): boolean {
  const t = (text || '').trim();
  if (!t) return false;
  if (isMetadataOnlySynthesisContext(t)) return false;
  if (isPendingAkteLazyExtractContext(t)) return false;
  if (isRecognizingAkteLazyExtractContext(t)) return false;
  if (isFailedAkteLazyExtractContext(t)) return false;
  return /Auszug \(UTF-8/.test(t);
}

/** Ein erfolgreicher Body darf durch einen spaeteren Loading-/Fehler-Render nicht verloren gehen. */
export function preferUsableAkteDocumentContext(
  previous: string | null | undefined,
  next: string | null | undefined,
): string | null {
  if (hasUsableAkteExtract(previous) && !hasUsableAkteExtract(next)) {
    return previous!.trim();
  }
  return next?.trim() || null;
}

/** ConvertService-Titel (~200 Zeichen) darf einen längeren pdf2text-Cache nicht ersetzen. */
export function preferLongerAkteExtract(
  previous: string | null | undefined,
  next: string | null | undefined,
): string | null {
  const a = (previous || '').trim();
  const b = (next || '').trim();
  if (!b) return a || null;
  if (!a) return b;
  return b.length >= a.length ? b : a;
}

function akteExtractBodyLength(text: string): number {
  const match = /Auszug \(UTF-8[^\n]*\):\n/u.exec(text);
  if (!match || match.index == null) return text.trim().length;
  return text.slice(match.index + match[0].length).trim().length;
}

/** Beide usable: den längeren Body behalten (OO-Titel vs. Lazy-Textlayer). */
export function preferRicherAkteDocumentContext(
  previous: string | null | undefined,
  next: string | null | undefined,
): string | null {
  if (hasUsableAkteExtract(previous) && hasUsableAkteExtract(next)) {
    const prevLen = akteExtractBodyLength(previous!);
    const nextLen = akteExtractBodyLength(next!);
    if (prevLen > nextLen + 80) return previous!.trim();
  }
  return preferUsableAkteDocumentContext(previous, next);
}

export function akteSelectedIdsNeedingExtract(args: {
  selectedIds: string[];
  cache: Record<string, { documentContext?: string | null } | undefined>;
  inFlightIds?: Iterable<string>;
  maxSelected?: number;
}): string[] {
  const max = typeof args.maxSelected === 'number' && args.maxSelected > 0
    ? Math.floor(args.maxSelected)
    : 5;
  const inFlight = new Set(
    [...(args.inFlightIds ?? [])].map((id) => id.trim()).filter(Boolean),
  );
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of args.selectedIds) {
    const id = raw.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    if (out.length >= max) break;
    if (inFlight.has(id)) continue;
    const ctx = args.cache[id]?.documentContext;
    if (hasUsableAkteExtract(ctx)) continue;
    if (isFailedAkteLazyExtractContext(ctx)) continue;
    if (isRecognizingAkteLazyExtractContext(ctx) && !isStaleRecognizingAkteLazyExtractContext(ctx)) {
      continue;
    }
    if (isPendingAkteLazyExtractContext(args.cache[id]?.documentContext) && inFlight.size > 0) {
      continue;
    }
    out.push(id);
  }
  return out;
}

export function truncateAkteLazyExtractText(
  raw: string,
  limit: number,
): { text: string; truncated: boolean } {
  if (raw.length <= limit) return { text: raw, truncated: false };
  const slice = raw.slice(0, limit);
  const lastWs = slice.search(/\s\S*$/);
  const cut = lastWs > limit * 0.7 ? slice.slice(0, lastWs) : slice;
  return { text: `${cut.trimEnd()}\n…[hier abgeschnitten]`, truncated: true };
}

/** Auch ein im Editor geladener Volltext wird im Akten-Cache auf denselben Body-Cap gebracht. */
export function capAkteExtractDocumentContext(
  context: string | null | undefined,
  limit = PORTAL_AKTE_LAZY_EXTRACT_MAX_CHARS,
): string | null {
  const raw = context?.trim() || '';
  if (!raw) return null;
  const match = /^([\s\S]*?Auszug \(UTF-8[^\n]*\):\n)([\s\S]*)$/u.exec(raw);
  if (!match) return raw;
  const { text } = truncateAkteLazyExtractText(match[2].trim(), limit);
  return `${match[1]}${text}`;
}

export function akteExtractSourceLabel(source?: string | null, viaOnlyOffice = false): string {
  const normalized = String(source || '').trim().toLowerCase();
  if (normalized.includes('ocr_image')) return ' via lokale OCR (Bild)';
  if (normalized.includes('ocr_pdf')) return ' via lokale OCR (Scan-PDF)';
  if (normalized.includes('pdf_text')) return ' via lokale PDF-Textebene';
  if (normalized.includes('onlyoffice')) return ' via OnlyOffice ConvertService';
  return viaOnlyOffice ? ' via OnlyOffice ConvertService' : '';
}

export function buildAkteLazyExtractDocumentContext(args: {
  title: string;
  nc_path: string | null;
  extractText: string;
  viaOnlyOffice: boolean;
  extractSource?: string | null;
  maxChars?: number;
}): string {
  const title = args.title.trim() || 'Dokument';
  const source = args.nc_path?.trim() || '(unbekannt)';
  const limit = args.maxChars ?? PORTAL_AKTE_LAZY_EXTRACT_MAX_CHARS;
  const { text, truncated } = truncateAkteLazyExtractText(args.extractText.trim(), limit);
  const via = akteExtractSourceLabel(args.extractSource, args.viaOnlyOffice);
  return [
    `Dokument: ${title}`,
    `Quelle: ${source}`,
    '',
    `Auszug (UTF-8${via}${truncated ? `, gekürzt auf ${limit} Zeichen` : ''}):`,
    text,
  ].join('\n');
}

export function buildPendingAkteLazyExtractContext(meta: {
  title: string;
  nc_path: string | null;
}): string {
  const title = meta.title.trim() || 'Dokument';
  const source = meta.nc_path?.trim() || '(unbekannt)';
  return [
    `Dokument: ${title}`,
    `Quelle: ${source}`,
    '',
    `Hinweis: ${PENDING_HINT} — OnlyOffice muss dafür nicht geöffnet sein.`,
  ].join('\n');
}

export function buildRecognizingAkteLazyExtractContext(meta: {
  title: string;
  nc_path: string | null;
  startedAtMs?: number;
}): string {
  const title = meta.title.trim() || 'Dokument';
  const source = meta.nc_path?.trim() || '(unbekannt)';
  const started = Number.isFinite(meta.startedAtMs) ? Number(meta.startedAtMs) : Date.now();
  return [
    `Dokument: ${title}`,
    `Quelle: ${source}`,
    '',
    `Hinweis: ${RECOGNIZING_HINT} Texterkennung läuft im Hintergrund — OnlyOffice-Anzeige liefert keinen Auszug.`,
    `${PORTAL_AKTE_OCR_STARTED_MARKER}${started}`,
  ].join('\n');
}

export function buildFailedAkteLazyExtractContext(meta: {
  title: string;
  nc_path: string | null;
  reason?: string;
}): string {
  const title = meta.title.trim() || 'Dokument';
  const source = meta.nc_path?.trim() || '(unbekannt)';
  const reason = meta.reason?.trim();
  const isImage = /\.(?:jpe?g|png)$/i.test(title);
  const noReadableText = /(?:kein|ohne|zu wenig).*(?:auslesbar|lesbar|text)|scan_without_text|image_without_text/i.test(
    reason || '',
  );
  const followUp = noReadableText
    ? isImage
      ? 'Bild in der Akte, aber kein auslesbarer Text. ARKO darf die Szene nicht erraten.'
      : 'Scan ohne auslesbaren Text. ARKO darf aus dem Dateinamen keinen Inhalt ableiten.'
    : isImage
      ? 'Bild bleibt in der Akte; erneut anhaken versucht die lokale OCR nochmals.'
      : 'Auszug in den Chat kopieren, falls die Texterkennung keinen Text findet.';
  return [
    `Dokument: ${title}`,
    `Quelle: ${source}`,
    '',
    `Hinweis: ${FAILED_HINT} werden${reason ? ` (${reason})` : ''}. ${followUp}`,
  ].join('\n');
}

/**
 * Klasse DISCRIMINATED_HTTP_RESULT (fail-closed).
 * Polar: VERBOTEN status/error/pending auf der Union oder am Success-Arm
 * (UI darf Erfolg nicht als HTTP-Fehler lesen). ERLAUBT: nach
 * `isAkteLazyExtractHttpFailure` die Failure-Felder; nach Success `textBase64`.
 * `!result.ok` reicht ohne strictNullChecks nicht als Narrowing.
 */
export type AkteLazyExtractHttpResult =
  | {
    ok: true;
    textBase64: string;
    source?: string;
    ocrUsed?: boolean;
    bytes?: number;
    rawBytes?: number;
    truncated?: boolean;
    pdfPages?: number;
  }
  | { ok: false; pending: true; jobId: string; status: number; error?: string }
  | { ok: false; status: number; error?: string; pending?: false };

export type AkteLazyExtractHttpFailure = Extract<AkteLazyExtractHttpResult, { ok: false }>;

export function isPendingAkteExtractHttpResult(
  result: AkteLazyExtractHttpResult,
): result is { ok: false; pending: true; jobId: string; status: number; error?: string } {
  return result.ok === false && result.pending === true && Boolean(result.jobId);
}

export function isAkteLazyExtractHttpFailure(
  result: AkteLazyExtractHttpResult,
): result is AkteLazyExtractHttpFailure {
  return result.ok === false;
}

type AkteLazyExtractHttpSuccess = Extract<AkteLazyExtractHttpResult, { ok: true }>;
type PolarSuccessHasNo<K extends string> = K extends keyof AkteLazyExtractHttpSuccess
  ? ['DISCRIMINATED_HTTP_RESULT: success arm must not declare', K]
  : true;
const _polarNoStatus: PolarSuccessHasNo<'status'> = true;
const _polarNoError: PolarSuccessHasNo<'error'> = true;
const _polarNoPending: PolarSuccessHasNo<'pending'> = true;
void _polarNoStatus;
void _polarNoError;
void _polarNoPending;

export function isSoftAkteExtractHttpStatus(status: number): boolean {
  return status === 200 || status === 202;
}

/**
 * HTTP-Form für Extract/Plaintext — fetch injizierbar, kein Vite.
 * 422 = leeres/nicht unterstütztes Doc (kein Retry-Sturm).
 */
function isAbortError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const name = 'name' in err ? String((err as { name?: unknown }).name || '') : '';
  return name === 'AbortError' || name === 'TimeoutError';
}

export async function fetchAkteDocumentExtractText(args: {
  fetchFn: typeof fetch;
  url: string;
  body: Record<string, string>;
  headers: Record<string, string>;
  signal?: AbortSignal;
  timeoutMs?: number;
}): Promise<AkteLazyExtractHttpResult> {
  const timeoutMs = args.timeoutMs ?? PORTAL_AKTE_LAZY_EXTRACT_TIMEOUT_MS;
  const extra = new AbortController();
  let timedOut = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const onOuterAbort = () => extra.abort();
  if (args.signal) {
    if (args.signal.aborted) extra.abort();
    else args.signal.addEventListener('abort', onOuterAbort, { once: true });
  }
  if (timeoutMs > 0) {
    timer = setTimeout(() => {
      timedOut = true;
      extra.abort();
    }, timeoutMs);
  }
  try {
    const res = await args.fetchFn(args.url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...args.headers,
      },
      credentials: 'omit',
      body: JSON.stringify(args.body),
      signal: extra.signal,
    });
    let data: {
      ok?: boolean;
      pending?: boolean;
      job_id?: string;
      jobId?: string;
      text_utf8_base64?: string;
      error?: string;
      source?: string;
      ocr_used?: boolean;
      bytes?: number;
      raw_bytes?: number;
      truncated?: boolean;
      pdf_pages?: number;
    } = {};
    try {
      data = (await res.json()) as typeof data;
    } catch {
      /* leerer Body / Connection Closed mit Halbantwort */
    }
    const jobId = String(data.job_id || data.jobId || '').trim();
    if ((data.pending === true || res.status === 202) && jobId) {
      return { ok: false, pending: true, jobId, status: res.status || 202 };
    }
    if (!res.ok || !data.ok || typeof data.text_utf8_base64 !== 'string') {
      return {
        ok: false,
        status: res.status,
        error: data.error?.trim() || (res.status ? String(res.status) : 'Verbindung geschlossen'),
      };
    }
    return {
      ok: true,
      textBase64: data.text_utf8_base64,
      source: data.source?.trim() || undefined,
      ocrUsed: data.ocr_used === true,
      bytes: Number.isFinite(Number(data.bytes)) ? Number(data.bytes) : undefined,
      rawBytes: Number.isFinite(Number(data.raw_bytes)) ? Number(data.raw_bytes) : undefined,
      truncated: data.truncated === true,
      pdfPages: Number(data.pdf_pages) > 0 ? Number(data.pdf_pages) : undefined,
    };
  } catch (err) {
    if (args.signal?.aborted && !timedOut) throw err;
    if (timedOut || isAbortError(err)) {
      return { ok: false, status: 504, error: 'Zeitüberschreitung' };
    }
    return { ok: false, status: 0, error: 'Verbindung geschlossen' };
  } finally {
    if (timer) clearTimeout(timer);
    args.signal?.removeEventListener('abort', onOuterAbort);
  }
}

function sleepMs(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      const err = new Error('aborted');
      err.name = 'AbortError';
      reject(err);
      return;
    }
    const timer = setTimeout(resolve, ms);
    const onAbort = () => {
      clearTimeout(timer);
      const err = new Error('aborted');
      err.name = 'AbortError';
      reject(err);
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

export async function pollAkteDocumentExtractJob(args: {
  fetchFn: typeof fetch;
  url: string;
  jobId: string;
  customerId?: string;
  extraBody?: Record<string, string>;
  headers: Record<string, string>;
  signal?: AbortSignal;
}): Promise<AkteLazyExtractHttpResult> {
  const body: Record<string, string> = {
    job_id: args.jobId,
    ...(args.extraBody || {}),
  };
  if (args.customerId) body.customerId = args.customerId;
  return fetchAkteDocumentExtractText({
    fetchFn: args.fetchFn,
    url: args.url,
    body,
    headers: args.headers,
    signal: args.signal,
    timeoutMs: 15_000,
  });
}

/** Pollt einen offenen OCR-Job, bis Text, No-Text oder Budget. */
export async function waitForAkteDocumentExtractJob(args: {
  fetchFn: typeof fetch;
  url: string;
  jobId: string;
  customerId?: string;
  extraBody?: Record<string, string>;
  headers: Record<string, string>;
  signal?: AbortSignal;
  intervalMs?: number;
  maxMs?: number;
}): Promise<AkteLazyExtractHttpResult> {
  const intervalMs = args.intervalMs ?? PORTAL_AKTE_OCR_POLL_INTERVAL_MS;
  const maxMs = args.maxMs ?? PORTAL_AKTE_OCR_POLL_MAX_MS;
  const started = Date.now();
  let last: AkteLazyExtractHttpResult = {
    ok: false,
    pending: true,
    jobId: args.jobId,
    status: 202,
  };
  while (Date.now() - started < maxMs) {
    if (args.signal?.aborted) {
      const err = new Error('aborted');
      err.name = 'AbortError';
      throw err;
    }
    last = await pollAkteDocumentExtractJob(args);
    if (last.ok || !isPendingAkteExtractHttpResult(last)) return last;
    await sleepMs(intervalMs, args.signal);
  }
  return {
    ok: false,
    status: 422,
    error: 'Scan ohne auslesbaren Text',
  };
}

/** Browser-Cache der fertigen Auszüge — Reload darf OCR nicht nochmal anstoßen. Kein neues PB. */
/** v3: 64-KB-Puffer-Caches (Specimen 65172 von 102701) nicht hydratisieren. */
export const PORTAL_AKTE_EXTRACT_CACHE_STORAGE_PREFIX = 'arkorbit_portal_akte_extract_cache_v3__';
export const PORTAL_AKTE_EXTRACT_CACHE_MAX_ENTRIES = 40;
/** Voller Extract nur für die jüngsten Treffer — localStorage-Quota. */
export const PORTAL_AKTE_EXTRACT_FULLTEXT_MAX_ENTRIES = 8;

export type AkteDocContextCacheEntry = {
  documentContext: string | null;
  documentExtractFingerprint: string | null;
  title: string;
  nc_path: string | null;
  fullExtractText?: string | null;
  extractPages?: number | null;
  extractRawChars?: number | null;
  extractTruncated?: boolean;
  extractSource?: string | null;
};

export type AkteExtractCacheEntry = {
  documentId: string;
  fingerprint: string;
  documentContext: string;
  title: string;
  nc_path: string | null;
  savedAt: number;
  fullExtractText?: string;
  extractPages?: number;
  extractRawChars?: number;
  extractTruncated?: boolean;
  extractSource?: string;
};

export type AkteExtractCacheStore = {
  version: 1;
  byId: Record<string, AkteExtractCacheEntry>;
};

export type AkteExtractCacheKv = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
};

export function akteExtractCacheStorageKey(customerId: string): string {
  const id = customerId.trim() || 'unknown';
  return `${PORTAL_AKTE_EXTRACT_CACHE_STORAGE_PREFIX}${id}`;
}

/** PB-Revision: updated + Größe + Pfad + OO-Revision — nicht die Chat-Fingerprint-Kette. */
export function buildAkteDocumentRevFingerprint(args: {
  updated?: string | null;
  fileSize?: number | null;
  ncPath?: string | null;
  lastRevisionId?: number | null;
}): string {
  return [
    String(args.updated || '').trim(),
    args.fileSize == null || !Number.isFinite(args.fileSize) ? '' : String(args.fileSize),
    String(args.ncPath || '').trim(),
    args.lastRevisionId == null || !Number.isFinite(args.lastRevisionId)
      ? ''
      : String(args.lastRevisionId),
  ].join('|');
}

export function isAkteDocumentRevFingerprintReady(fingerprint: string | null | undefined): boolean {
  return /[^|]/.test(String(fingerprint || ''));
}

function emptyAkteExtractCacheStore(): AkteExtractCacheStore {
  return { version: 1, byId: {} };
}

export function parseAkteExtractCacheStore(raw: string | null | undefined): AkteExtractCacheStore {
  if (!raw || !raw.trim()) return emptyAkteExtractCacheStore();
  try {
    const parsed = JSON.parse(raw) as Partial<AkteExtractCacheStore>;
    if (parsed.version !== 1 || !parsed.byId || typeof parsed.byId !== 'object') {
      return emptyAkteExtractCacheStore();
    }
    const byId: Record<string, AkteExtractCacheEntry> = {};
    for (const [id, entry] of Object.entries(parsed.byId)) {
      const documentId = String(entry?.documentId || id).trim();
      const fingerprint = String(entry?.fingerprint || '').trim();
      const documentContext = String(entry?.documentContext || '').trim();
      if (!documentId || !fingerprint || !hasUsableAkteExtract(documentContext)) continue;
      const full = typeof entry?.fullExtractText === 'string' ? entry.fullExtractText.trim() : '';
      const pages = Number(entry?.extractPages);
      const rawChars = Number(entry?.extractRawChars);
      byId[documentId] = {
        documentId,
        fingerprint,
        documentContext,
        title: String(entry?.title || '').trim() || documentId,
        nc_path: typeof entry?.nc_path === 'string' ? entry.nc_path : entry?.nc_path === null ? null : null,
        savedAt: Number.isFinite(entry?.savedAt) ? Number(entry.savedAt) : 0,
        ...(full ? { fullExtractText: full } : {}),
        ...(Number.isFinite(pages) && pages > 0 ? { extractPages: pages } : {}),
        ...(Number.isFinite(rawChars) && rawChars > 0 ? { extractRawChars: rawChars } : {}),
        ...(entry?.extractTruncated === true ? { extractTruncated: true } : {}),
        ...(typeof entry?.extractSource === 'string' && entry.extractSource.trim()
          ? { extractSource: entry.extractSource.trim() }
          : {}),
      };
    }
    return { version: 1, byId };
  } catch {
    return emptyAkteExtractCacheStore();
  }
}

function pruneAkteExtractCacheStore(store: AkteExtractCacheStore): AkteExtractCacheStore {
  const entries = Object.values(store.byId).sort((a, b) => b.savedAt - a.savedAt);
  const kept = entries.slice(0, PORTAL_AKTE_EXTRACT_CACHE_MAX_ENTRIES);
  const byId: Record<string, AkteExtractCacheEntry> = {};
  kept.forEach((entry, index) => {
    if (index < PORTAL_AKTE_EXTRACT_FULLTEXT_MAX_ENTRIES) {
      byId[entry.documentId] = entry;
      return;
    }
    const { fullExtractText: _drop, ...rest } = entry;
    void _drop;
    byId[entry.documentId] = rest;
  });
  return { version: 1, byId };
}

function browserAkteExtractCacheKv(): AkteExtractCacheKv | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

export function loadAkteExtractCacheStore(
  customerId: string,
  kv: AkteExtractCacheKv | null = browserAkteExtractCacheKv(),
): AkteExtractCacheStore {
  if (!customerId.trim() || !kv) return emptyAkteExtractCacheStore();
  try {
    return parseAkteExtractCacheStore(kv.getItem(akteExtractCacheStorageKey(customerId)));
  } catch {
    return emptyAkteExtractCacheStore();
  }
}

export function saveUsableAkteExtractToCache(
  customerId: string,
  entry: Omit<AkteExtractCacheEntry, 'savedAt'> & { savedAt?: number },
  kv: AkteExtractCacheKv | null = browserAkteExtractCacheKv(),
): boolean {
  if (!customerId.trim() || !kv) return false;
  if (!isAkteDocumentRevFingerprintReady(entry.fingerprint)) return false;
  const documentContext = capAkteExtractDocumentContext(entry.documentContext);
  if (!hasUsableAkteExtract(documentContext)) return false;
  const documentId = entry.documentId.trim();
  if (!documentId) return false;
  try {
    const nextEntry: AkteExtractCacheEntry = {
      documentId,
      fingerprint: entry.fingerprint.trim(),
      documentContext: documentContext!,
      title: entry.title.trim() || documentId,
      nc_path: entry.nc_path ?? null,
      savedAt: entry.savedAt ?? Date.now(),
    };
    const full = entry.fullExtractText?.trim();
    if (full) nextEntry.fullExtractText = full;
    if (entry.extractPages && entry.extractPages > 0) nextEntry.extractPages = entry.extractPages;
    if (entry.extractRawChars && entry.extractRawChars > 0) nextEntry.extractRawChars = entry.extractRawChars;
    if (entry.extractTruncated) nextEntry.extractTruncated = true;
    if (entry.extractSource?.trim()) nextEntry.extractSource = entry.extractSource.trim();
    const store = pruneAkteExtractCacheStore({
      version: 1,
      byId: {
        ...loadAkteExtractCacheStore(customerId, kv).byId,
        [documentId]: nextEntry,
      },
    });
    try {
      kv.setItem(akteExtractCacheStorageKey(customerId), JSON.stringify(store));
      return true;
    } catch {
      const slim: AkteExtractCacheStore = {
        version: 1,
        byId: Object.fromEntries(
          Object.entries(store.byId).map(([id, row]) => {
            const { fullExtractText: _drop, ...rest } = row;
            void _drop;
            return [id, rest];
          }),
        ),
      };
      kv.setItem(akteExtractCacheStorageKey(customerId), JSON.stringify(slim));
      return true;
    }
  } catch {
    return false;
  }
}

export function dropAkteExtractCacheEntries(
  customerId: string,
  documentIds: string[],
  kv: AkteExtractCacheKv | null = browserAkteExtractCacheKv(),
): void {
  if (!customerId.trim() || !kv || documentIds.length === 0) return;
  try {
    const store = loadAkteExtractCacheStore(customerId, kv);
    let changed = false;
    for (const raw of documentIds) {
      const id = raw.trim();
      if (!id || !store.byId[id]) continue;
      delete store.byId[id];
      changed = true;
    }
    if (!changed) return;
    kv.setItem(akteExtractCacheStorageKey(customerId), JSON.stringify(store));
  } catch {
    /* Quota / privater Modus */
  }
}

/**
 * Füllt den React-Cache aus localStorage.
 * Fingerprint bekannt + abweichend → nicht hydratisieren (Datei geändert).
 * Fingerprint noch unbekannt → trotzdem hydratisieren (Strg+F5, PB-Liste kommt gleich).
 */
export function hydrateAkteExtractCacheFromPersisted(args: {
  cache: Record<string, AkteDocContextCacheEntry | undefined>;
  persisted: Record<string, AkteExtractCacheEntry>;
  fingerprintsByDocumentId: Record<string, string>;
  selectedIds: string[];
}): {
  next: Record<string, AkteDocContextCacheEntry>;
  changed: boolean;
  hydratedIds: string[];
  staleIds: string[];
} {
  const next: Record<string, AkteDocContextCacheEntry> = { ...args.cache } as Record<
    string,
    AkteDocContextCacheEntry
  >;
  let changed = false;
  const hydratedIds: string[] = [];
  const staleIds: string[] = [];
  const seen = new Set<string>();
  for (const raw of args.selectedIds) {
    const id = raw.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const persisted = args.persisted[id];
    const currentFp = args.fingerprintsByDocumentId[id];
    const fpReady = isAkteDocumentRevFingerprintReady(currentFp);
    if (
      persisted
      && fpReady
      && persisted.fingerprint
      && persisted.fingerprint !== currentFp
    ) {
      staleIds.push(id);
      const existing = next[id];
      if (existing && existing.documentExtractFingerprint === persisted.fingerprint) {
        delete next[id];
        changed = true;
      }
      continue;
    }
    if (hasUsableAkteExtract(next[id]?.documentContext)) continue;
    if (!persisted || !hasUsableAkteExtract(persisted.documentContext)) continue;
    next[id] = {
      documentContext: capAkteExtractDocumentContext(persisted.documentContext),
      documentExtractFingerprint: persisted.fingerprint,
      title: persisted.title,
      nc_path: persisted.nc_path,
      ...(persisted.fullExtractText ? { fullExtractText: persisted.fullExtractText } : {}),
      ...(persisted.extractPages ? { extractPages: persisted.extractPages } : {}),
      ...(persisted.extractRawChars ? { extractRawChars: persisted.extractRawChars } : {}),
      ...(persisted.extractTruncated ? { extractTruncated: true } : {}),
      ...(persisted.extractSource ? { extractSource: persisted.extractSource } : {}),
    };
    changed = true;
    hydratedIds.push(id);
  }
  return { next, changed, hydratedIds, staleIds };
}
